import {PogisDocument, Regulation, RegulatoryDocument} from 'types';
import {TFunction} from 'react-i18next';
import Excel, {RichText, Table, Worksheet} from 'exceljs';
import {funcDecodeHex24ToDocId} from 'components/InfinityList';
import {
	createZIPXL,
	renderArrayXL,
	renderAttachmentsXL,
	renderDateXL,
	renderHeaderXL,
	renderHTMLStringXL,
	renderPhaseDateXL,
	renderRichtText,
} from 'components/InfinityList/Excel/ExcelExportCommon';
import {EntityListColumn} from '../EntityList';

interface IRegulationDetailPage {
	(
		entityListItems: any[],
		pageDetails: any,
		columns: EntityListColumn[],
		t: TFunction,
	): Promise<void>;
}

interface IRenderTable {
	(
		arrRegDocs: RegulatoryDocument[],
		sheet: Worksheet,
		table: Table,
		intRowCountTop: number,
		t: TFunction,
	): number;
}

export const handleClickRegulationDetailPage: IRegulationDetailPage = async (
	entityListItems,
	pageDetails,
	columns,
	t,
) => {
	const arrTables: string[] = [];
	const arrImagesUri: string[] = [];
	const arrImagesName: string[] = [];

	const arrRegDoc = entityListItems as RegulatoryDocument[];
	const regulation = pageDetails as Regulation;

	const tRegulationList = {
		ns: 'features/regulations',
		keyPrefix: 'RegulationsList',
	};

	const tRegulationDetail = {
		ns: 'features/regulations',
		keyPrefix: 'RegulationDetail',
	};

	const workbook = new Excel.Workbook();
	const arrColWidth: number[] = [50, 100, ...new Array(14).fill(25)];

	const strPageName = t('Documents', tRegulationDetail);
	const sheet = workbook.addWorksheet('TRACE_' + strPageName);

	renderHeaderXL(workbook, sheet, arrColWidth, strPageName, t);

	const getHeading = (rawHeading: string) => {
		return t(rawHeading, tRegulationList);
	};

	const getHeadingDetail = (rawHeading: string) => {
		return t(rawHeading, tRegulationDetail);
	};

	let intRowCount = 4;
	let row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	row.getCell('B').value = renderRichtText(
		getHeading('RegulationNumber'),
		regulation.regulationNumber ?? '',
	);

	row.getCell('C').value = renderRichtText(
		getHeading('Name'),
		regulation.name ?? '',
	);

	intRowCount++;
	row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	row.getCell('B').value = renderRichtText(
		t('ContactPersonLabel', tRegulationDetail),
		regulation.contactPersons.map(a => a.name).join(';\n'),
	);

	row.getCell('C').value = renderHTMLStringXL(
		getHeadingDetail('ShortSummaryLabel'),
		regulation.summary,
	);

	intRowCount++;
	row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	row.getCell('B').value = renderRichtText(
		t('MainKeywordsLabel', tRegulationDetail),
		renderArrayXL(regulation.mainKeywords),
	);

	row.getCell('C').value = renderRichtText(
		t('KeyWordsLabel', tRegulationDetail),
		renderArrayXL(regulation.keywords),
	);

	row.getCell('D').value = renderRichtText(
		t('MarketsLabel', tRegulationDetail),
		renderArrayXL(regulation.markets),
	);

	intRowCount = renderAttachmentsXL(
		intRowCount,
		getHeadingDetail('AttachmentsLabel'),
		regulation.attachments,
		sheet,
		t,
	);

	intRowCount++;
	intRowCount++;

	const columnNames = columns.slice(2, -2).map(x => x.name);

	const columnsXL = columnNames.map(x => {
		return {name: x, filterButton: true};
	});

	sheet.addTable({
		name: 'tableRegulatoryDocuments',
		ref: 'B' + String(intRowCount),
		style: {
			showRowStripes: true,
		},
		columns: columnsXL,
		rows: [],
	});

	const table = sheet.getTable('tableRegulatoryDocuments');
	if (arrRegDoc.length === 0) {
		table.addRow([...new Array(columnsXL.length).fill('')]);
	}

	intRowCount++;
	intRowCount = renderTable(arrRegDoc, sheet, table, intRowCount, t);
	table.commit();

	const strZipName = strPageName + '_' + funcDecodeHex24ToDocId(regulation.id);
	await createZIPXL(
		workbook,
		strZipName,
		t,
		arrTables,
		arrImagesUri,
		arrImagesName,
		regulation.attachments!,
	);
};

const renderTable: IRenderTable = (
	arrRegDoc,
	sheet,
	table,
	intRowCountTop,
	t,
) => {
	const tPogis = {
		ns: 'features/pogisdocuments',
		keyPrefix: 'Reference',
	};
	let intRowCount = intRowCountTop;
	let row = sheet.getRow(intRowCount);
	for (let ii = 0; ii < arrRegDoc.length; ii++) {
		row = sheet.getRow(intRowCount);
		row.alignment = {wrapText: true, vertical: 'top'};

		const iRegDoc = arrRegDoc[ii];
		const strPogisIds =
			iRegDoc.pogisDocuments !== undefined && iRegDoc.pogisDocuments.length > 0
				? '\n' +
				  t('pogisDocuments', tPogis) +
				  ':\n' +
				  iRegDoc.pogisDocuments.map(x => x.pogisId).join(';\n')
				: '';
		const strPogis = iRegDoc.pogisExists ? t('yes', tPogis) + strPogisIds : '';

		table.addRow([
			iRegDoc.name,
			strPogis,
			t(iRegDoc.status ?? '', {
				ns: 'common/enums',
				keyPrefix: 'RegulatoryDocumentStatus',
			}),
			t(iRegDoc.disassemblyStatus ?? '', {
				ns: 'common/enums',
				keyPrefix: 'RegulatoryDocumentDisassemblyStatus',
			}),
			t(iRegDoc.workflow?.status ?? '', {
				ns: 'common/enums',
				keyPrefix: 'WorkflowStatus',
			}),
			renderArrayXL(iRegDoc.keywords),
			renderDateXL(iRegDoc.dateNewRegistration),
			renderDateXL(iRegDoc.dateNewTypes),
			renderDateXL(iRegDoc.dateEffective),
			iRegDoc.modelYear ?? '',
			renderPhaseDateXL(iRegDoc.phaseIn),
			renderPhaseDateXL(iRegDoc.phaseOut),
			renderDateXL(iRegDoc.dateExpiration),
			renderDateXL(iRegDoc.modifiedAt),
		]);
		intRowCount++;
	}

	return intRowCount;
};
